import './App.scss';
import { useState, useRef, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { 
  FaPhone, 
  FaWhatsapp, 
  FaEnvelope, 
  FaFacebookF, 
  FaInstagram,
  FaAddressCard,
  FaHouse,
  FaMoneyBillWave,
  FaKey,
  FaCircleCheck,
  FaSpinner,
  FaXmark
} from 'react-icons/fa6';
import { IoShareSocial } from 'react-icons/io5';

function App() {
  const [activeSection, setActiveSection] = useState('home');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [activeServiceModal, setActiveServiceModal] = useState(null);
  const formRef = useRef();

  // Bloquear scroll del body cuando hay un modal abierto
  useEffect(() => {
    if (isImageModalOpen || activeServiceModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    // Cleanup al desmontar
    return () => {
      document.body.style.overflow = '';
    };
  }, [isImageModalOpen, activeServiceModal]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Función para descargar vCard (Guardar Contacto)
  const downloadVCard = () => {
    const vCardData = `BEGIN:VCARD
VERSION:3.0
FN:Cecilia Rodriguez
N:Rodriguez;Cecilia;;;
ORG:eXp Realty México
TITLE:Agente Inmobiliario
TEL;TYPE=CELL:+5215513762453
EMAIL:cecilia.rodriguez@expmexico.mx
URL:https://cecilia-rodriguez.vercel.app/
URL:https://www.facebook.com/cecilia.rodriguez.77736/
URL:https://www.instagram.com/cecyrodriguez.cdmx/
NOTE:Agente inmobiliaria en CDMX - Vende, Compra o Renta con seguridad y respaldo internacional. Tarjeta digital: https://cecilia-rodriguez.vercel.app/
END:VCARD`;

    const blob = new Blob([vCardData], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Cecilia_Rodriguez_Contacto.vcf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Configuración de EmailJS
      // Para configurar:
      // 1. Crea una cuenta en https://www.emailjs.com/
      // 2. Crea un servicio de email (Gmail, Outlook, etc.)
      // 3. Crea una plantilla de email
      // 4. Reemplaza estos valores con los tuyos:
      const serviceId = 'service_2tp4nbc'; // Obtén esto de EmailJS Dashboard
      const templateId = 'template_d2ip4fd'; // Obtén esto de EmailJS Dashboard  
      const publicKey = 'x0hB0-4sbmZjA__gr'; // Obtén esto de EmailJS Dashboard

      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        from_phone: formData.phone,
        message: formData.message,
        to_email: 'cecilia.rodriguez@expmexico.mx'
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error al enviar:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="business-card">
      {/* Image Modal/Lightbox */}
      {isImageModalOpen && (
        <div className="image-modal" onClick={() => setIsImageModalOpen(false)}>
          <button className="modal-close" onClick={() => setIsImageModalOpen(false)}>
            <FaXmark />
          </button>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={`${process.env.PUBLIC_URL}/images/perfil.jpg`} alt="Cecilia Rodriguez" />
          </div>
        </div>
      )}

      {/* Service Info Modal */}
      {activeServiceModal && (
        <div className="service-modal" onClick={() => setActiveServiceModal(null)}>
          <div className="service-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setActiveServiceModal(null)}>
              <FaXmark />
            </button>
            
            {activeServiceModal === 'venta' && (
              <>
                <div 
                  className="modal-header-image"
                  style={{
                    backgroundImage: `linear-gradient(135deg, rgba(30, 58, 95, 0.7) 0%, rgba(197, 165, 114, 0.6) 100%), url(${process.env.PUBLIC_URL}/images/vende.webp)`
                  }}
                >
                  <FaHouse className="modal-icon" />
                  <h3>Vender tu Propiedad</h3>
                  <p className="modal-quote">"Tu hogar merece el mejor precio y la mejor estrategia."</p>
                </div>
                <div className="modal-body">
                  <p>
                    📸 Fotografía profesional y videos con drones<br/>
                    💰 Valuación precisa para maximizar tu ganancia<br/>
                    🌎 Exposición en múltiples plataformas internacionales<br/>
                    📋 Te acompaño en toda la documentación legal<br/>
                    ⚡ Proceso rápido y sin complicaciones
                  </p>
                  <p className="modal-cta">¡Vende con confianza y obtén el valor real de tu propiedad!</p>
                </div>
              </>
            )}
            
            {activeServiceModal === 'compra' && (
              <>
                <div 
                  className="modal-header-image"
                  style={{
                    backgroundImage: `linear-gradient(135deg, rgba(30, 58, 95, 0.7) 0%, rgba(197, 165, 114, 0.6) 100%), url(${process.env.PUBLIC_URL}/images/compra.webp)`
                  }}
                >
                  <FaMoneyBillWave className="modal-icon" />
                  <h3>Comprar tu Hogar Ideal</h3>
                  <p className="modal-quote">"Encontrar tu hogar perfecto es mi misión."</p>
                </div>
                <div className="modal-body">
                  <p>
                    🏡 Acceso a propiedades exclusivas antes que nadie<br/>
                    💡 Asesoría para elegir la mejor inversión<br/>
                    🔍 Búsqueda personalizada según tus necesidades<br/>
                    💼 Negociación experta para obtener el mejor precio<br/>
                    🤝 Apoyo con créditos hipotecarios y financiamiento
                  </p>
                  <p className="modal-cta">¡Hagamos realidad el hogar que siempre soñaste!</p>
                </div>
              </>
            )}
            
            {activeServiceModal === 'renta' && (
              <>
                <div 
                  className="modal-header-image"
                  style={{
                    backgroundImage: `linear-gradient(135deg, rgba(30, 58, 95, 0.7) 0%, rgba(197, 165, 114, 0.6) 100%), url(${process.env.PUBLIC_URL}/images/Renta.webp)`
                  }}
                >
                  <FaKey className="modal-icon" />
                  <h3>Rentar con Seguridad</h3>
                  <p className="modal-quote">"Protejo tus intereses en cada contrato."</p>
                </div>
                <div className="modal-body">
                  <p>
                    ✅ Verificación completa de inquilinos/propietarios<br/>
                    📄 Contratos legales claros y seguros<br/>
                    🏘️ Propiedades en las mejores zonas de CDMX<br/>
                    💰 Precios justos y transparentes<br/>
                    📞 Seguimiento continuo durante todo el proceso
                  </p>
                  <p className="modal-cta">¡Renta sin preocupaciones con respaldo profesional!</p>
                </div>
              </>
            )}
            
            <button className="btn btn-primary" onClick={() => {
              setActiveServiceModal(null);
              window.open('https://wa.me/5215513762453?text=Hola%20Cecilia%2C%20me%20interesa%20el%20servicio%20de%20' + activeServiceModal, '_blank');
            }}>
              <FaWhatsapp /> Quiero más información
            </button>
          </div>
        </div>
      )}

      {/* Header Profile Section */}
      <div 
        className="profile-header"
        style={{
          background: `linear-gradient(135deg, rgba(30, 58, 95, 0.65) 0%, rgba(15, 39, 68, 0.95) 100%), url(${process.env.PUBLIC_URL}/images/casa.webp)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="profile-image" onClick={() => setIsImageModalOpen(true)}>
          <img src={`${process.env.PUBLIC_URL}/images/perfil.jpg`} alt="Cecilia Rodriguez" />
        </div>
        <h1 className="profile-name">Cecilia Rodriguez</h1>
        <h2 className="profile-title">Agente de eXp Realty México</h2>
        <p className="company-name">Vende, Compra o Renta</p>
        <p className="company-subtitle">Con seguridad y respaldo internacional</p>
        
        {/* Contact Info */}
        <div className="contact-info">
          <a href="tel:+5215513762453" className="contact-link">+52 1 55 1376 2453</a>
          <a href="mailto:cecilia.rodriguez@expmexico.mx" className="contact-link">
            cecilia.rodriguez@expmexico.mx
          </a>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button className="btn btn-primary" onClick={downloadVCard}>
            <FaAddressCard className="icon" /> GUARDAR CONTACTO
          </button>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions">
          <a href="tel:+5215513762453" className="quick-btn phone">
            <FaPhone className="icon" />
            <span>Llamar</span>
          </a>
          <a href="https://wa.me/5215513762453?text=Hola%20Cecilia%2C%20vi%20tu%20tarjeta%20de%20presentaci%C3%B3n%20y%20me%20gustar%C3%ADa%20obtener%20m%C3%A1s%20informaci%C3%B3n%20sobre%20tus%20servicios%20inmobiliarios." className="quick-btn whatsapp">
            <FaWhatsapp className="icon" />
            <span>WhatsApp</span>
          </a>
          <a href="mailto:cecilia.rodriguez@expmexico.mx" className="quick-btn email">
            <FaEnvelope className="icon" />
            <span>Email</span>
          </a>
          <button className="quick-btn share" onClick={() => {
            if (navigator.share) {
              navigator.share({ title: 'Mi Tarjeta', url: window.location.href });
            }
          }}>
            <IoShareSocial className="icon" />
            <span>Compartir</span>
          </button>
        </div>

        {/* Social Links */}
        <div className="social-links">
          <a href="https://www.facebook.com/cecilia.rodriguez.77736/" target="_blank" rel="noopener noreferrer" className="social-icon facebook" title="Facebook">
            <FaFacebookF />
          </a>
          <a href="https://www.instagram.com/cecyrodriguez.cdmx/" target="_blank" rel="noopener noreferrer" className="social-icon instagram" title="Instagram">
            <FaInstagram />
          </a>
        </div>
      </div>

      {/* Navigation */}
      <nav className="navigation">
        <button 
          className={activeSection === 'home' ? 'active' : ''} 
          onClick={() => setActiveSection('home')}
        >
          Home
        </button>
        <button 
          className={activeSection === 'about' ? 'active' : ''} 
          onClick={() => setActiveSection('about')}
        >
          Acerca de Mí
        </button>
        <button 
          className={activeSection === 'connect' ? 'active' : ''} 
          onClick={() => setActiveSection('connect')}
        >
          Conectar
        </button>
        <button 
          className={activeSection === 'contact' ? 'active' : ''} 
          onClick={() => setActiveSection('contact')}
        >
          Contacto
        </button>
      </nav>

      {/* Content Sections */}
      <div className="content-section">
        {activeSection === 'home' && (
          <div className="section-home">
            <h3>Contáctame hoy y descubre el valor de tener un agente eXp a tu lado</h3>
            <p>
              Agente inmobiliaria en Ciudad de México, especializada en compra, venta 
              y renta de propiedades con seguridad y respaldo internacional.
            </p>
            
            <div className="benefits-section">
              <h4>Beneficios de Trabajar Conmigo</h4>
              <ul className="benefits-list">
                <li>
                  <FaCircleCheck className="check-icon" />
                  <span>Acompañamiento de inicio a fin en tu operación</span>
                </li>
                <li>
                  <FaCircleCheck className="check-icon" />
                  <span>Asesoría 100% personalizada</span>
                </li>
                <li>
                  <FaCircleCheck className="check-icon" />
                  <span>Red internacional de agentes y compradores potenciales</span>
                </li>
                <li>
                  <FaCircleCheck className="check-icon" />
                  <span>Transacciones seguras y transparentes</span>
                </li>
                <li>
                  <FaCircleCheck className="check-icon" />
                  <span>Acceso a herramientas y estrategias de marketing digital</span>
                </li>
              </ul>
            </div>

            <div className="services">
              <div className="service-item" onClick={() => setActiveServiceModal('venta')}>
                <FaHouse className="service-icon" />
                <span>Venta</span>
              </div>
              <div className="service-item" onClick={() => setActiveServiceModal('compra')}>
                <FaMoneyBillWave className="service-icon" />
                <span>Compra</span>
              </div>
              <div className="service-item" onClick={() => setActiveServiceModal('renta')}>
                <FaKey className="service-icon" />
                <span>Renta</span>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'about' && (
          <div className="section-about">
            <h3>Acerca de Mí</h3>
            <p>
              Hola, soy Cecilia Rodriguez 👋
            </p>
            <p>
              Para mí, el sector inmobiliario no es solo mi profesión, es mi pasión. 
              Entiendo que comprar, vender o rentar una propiedad es una de las decisiones 
              más importantes de tu vida, y por eso me comprometo a estar contigo en cada paso del camino.
            </p>
            <p>
              Lo que más disfruto de mi trabajo es ver la emoción en los rostros de mis clientes 
              cuando encuentran el hogar perfecto o cuando logran vender su propiedad al mejor precio. 
              Cada historia es única y me encanta ser parte de ella.
            </p>
            <p>
              Con eXp Realty México, tengo acceso a una red internacional de profesionales, 
              herramientas de vanguardia y años de experiencia que pongo a tu servicio. 
              Pero más allá de eso, lo que realmente me define es mi compromiso genuino con tus metas.
            </p>
            <p>
              <strong>¿Mi objetivo?</strong> Que tu experiencia inmobiliaria sea memorable, 
              transparente y exitosa. Porque al final del día, tu confianza es mi mejor recompensa.
            </p>
          </div>
        )}

        {activeSection === 'connect' && (
          <div className="section-connect">
            <h3>Conecta Conmigo</h3>
            <p>Sígueme en mis redes sociales para estar al tanto de las mejores oportunidades inmobiliarias en CDMX.</p>
            <div className="connect-links">
              <a href="https://www.facebook.com/cecilia.rodriguez.77736/" target="_blank" rel="noopener noreferrer" className="connect-item facebook">
                <FaFacebookF className="icon" />
                <span>Facebook - Cecilia Rodriguez</span>
              </a>
              <a href="https://www.instagram.com/cecyrodriguez.cdmx/" target="_blank" rel="noopener noreferrer" className="connect-item instagram">
                <FaInstagram className="icon" />
                <span>Instagram - @cecyrodriguez.cdmx</span>
              </a>
              <a href="https://wa.me/5215513762453?text=Hola%20Cecilia%2C%20vi%20tu%20tarjeta%20de%20presentaci%C3%B3n%20y%20me%20gustar%C3%ADa%20obtener%20m%C3%A1s%20informaci%C3%B3n%20sobre%20tus%20servicios%20inmobiliarios." target="_blank" rel="noopener noreferrer" className="connect-item whatsapp">
                <FaWhatsapp className="icon" />
                <span>WhatsApp - +52 1 55 1376 2453</span>
              </a>
            </div>
          </div>
        )}

        {activeSection === 'contact' && (
          <div className="section-contact">
            <h3>Formulario de Contacto</h3>
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">Nombre</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Tu nombre"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="tu@email.com"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Teléfono</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="55 1234 5678"
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Mensaje</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="¿En qué puedo ayudarte?"
                  rows="5"
                  required
                ></textarea>
              </div>
              
              {submitStatus === 'success' && (
                <div className="alert alert-success">
                  <FaCircleCheck /> ¡Mensaje enviado exitosamente! Te contactaré pronto.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="alert alert-error">
                  Hubo un error al enviar. Por favor intenta de nuevo o contáctame directamente.
                </div>
              )}
              
              <button type="submit" className="btn btn-submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <FaSpinner className="spinner" /> Enviando...
                  </>
                ) : (
                  'Enviar Mensaje'
                )}
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="footer">
        <p>© 2025 Cecilia Rodriguez</p>
        <div className="footer-credits">
          <p>Diseño por <a href="https://thisissantidev.vercel.app/" target="_blank" rel="noopener noreferrer">thisissanti.dev</a></p>
          <p>¿Quieres una tarjeta de presentación? <a href="https://wa.me/5215512308811?text=Hola%2C%20vi%20la%20tarjeta%20de%20presentaci%C3%B3n%20que%20hiciste%20y%20me%20interesa%20una%20para%20mi%20negocio." target="_blank" rel="noopener noreferrer">Contáctame</a></p>
        </div>
      </footer>
    </div>
  );
}

export default App;
